import React, { useState } from "react";
import { Button, Card, Form, Input, Menu, message, Modal, Popconfirm, Select, Table, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Flex from "components/shared-components/Flex";
import { addDoc, collection, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { FIRESTORE_CUSTOMER_GROUPS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { firestore } from "../../../configs/FirebaseConfig";
import EllipsisDropdown from "../../../components/shared-components/EllipsisDropdown";
import dayjs from "dayjs";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { useCustomers } from "../../../data/hooks/firestore/useCustomers";
import { useCustomerGroups } from "../../../data/hooks/firestore/useCustomerGroups";
import { useProducts } from "../../../data/hooks/firestore/useProducts";
import { useAuth } from "../../../data/hooks/firebase/useAuth";

const { Option } = Select;

const Groups = () => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const { data: groups, loading: groupsLoading } = useCustomerGroups();
  const { customers: companies, isLoading: companiesLoading } = useCustomers();
  const { products, isLoading: productsLoading } = useProducts();
  const [addGroupForm] = Form.useForm();
  const [editGroupForm] = Form.useForm();
  const [addGroupModalVisible, setAddGroupModalVisible] = useState(false);
  const [editGroupModalVisible, setEditGroupModalVisible] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);

  const openEditModal = (group) => {
    setGroupToEdit(group);
    editGroupForm.setFieldsValue({
      name: group.name,
      customers: !!group.customers ? group.customers : [],
      products: !!group.products ? group.products : [],
    });
    setEditGroupModalVisible(true);
  };

  const handleUpdateGroup = async () => {
    let values = editGroupForm.getFieldsValue();
    let object = {
      name: values.name,
      customers: !!values.customers && values.customers.length > 0 ? values.customers : null,
      products: !!values.products && values.products.length > 0 ? values.products : null,
      updatedAt: dayjs().unix(),
      updatedBy: email,
    };
    try {
      await updateDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_CUSTOMER_GROUPS_TABLE, groupToEdit.id),
        object,
      );
      closeEditModal();
      message.success(t("group_saved"), 2);
    } catch (error) {
      console.error("Error updating group:", error);
      message.error(t("error_updating_group"));
    }
  };

  const deleteGroup = async (group) => {
    try {
      await deleteDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_CUSTOMER_GROUPS_TABLE, group.id),
      );
      message.success(t("group_removed"), 2);
    } catch (err) {
      console.error("Error deleting group:", err);
      message.error(t("error_deleting_group"));
    }
  };

  const closeAddModal = () => {
    setAddGroupModalVisible(false);
    addGroupForm.resetFields();
  };

  const closeEditModal = () => {
    setEditGroupModalVisible(false);
    setGroupToEdit(null);
    editGroupForm.resetFields();
  };

  const handleAddGroup = async () => {
    addGroupForm.validateFields().then(async (values) => {
      let object = {
        name: values.name,
        customers: !!values.customers && values.customers.length > 0 ? values.customers : null,
        products: !!values.products && values.products.length > 0 ? values.products : null,
        createdAt: dayjs().unix(),
        createdBy: email,
      };
      try {
        await addDoc(
          collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_CUSTOMER_GROUPS_TABLE),
          object,
        );
        closeAddModal();
      } catch (err) {
        console.error("Error adding group:", err);
        message.error(err.message);
      }
    });
  };

  const tableColumns = [
    {
      title: t("name"),
      dataIndex: "name",
    },
    {
      title: t("customers"),
      dataIndex: "customers",
      render: (customers) => <span>{!!customers ? customers.length : 0}</span>,
      sorter: {
        compare: (a, b) => (!!a.customers ? a.customers.length : 0) - (!!b.customers ? b.customers.length : 0),
      },
    },
    {
      title: t("products"),
      dataIndex: "products",
      render: (products) => <span>{!!products ? products.length : 0}</span>,
      sorter: {
        compare: (a, b) => (!!a.products ? a.products.length : 0) - (!!b.products ? b.products.length : 0),
      },
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, row) => (
        <div className="text-right">
          <EllipsisDropdown
            menu={
              <Menu>
                <Menu.Item onClick={() => openEditModal(row)}>
                  <Flex alignItems="center">
                    <EditOutlined />
                    <span style={{ marginLeft: 4 }}>{t("edit")}</span>
                  </Flex>
                </Menu.Item>
                <Menu.Item>
                  <Flex alignItems="center">
                    <DeleteOutlined />
                    <Popconfirm
                      placement="bottom"
                      title={t("confirm_delete_group", {
                        name: row.name,
                      })}
                      onConfirm={() => deleteGroup(row)}
                      okText={t("yes")}
                      cancelText={t("no")}
                    >
                      <span style={{ marginLeft: 4 }}>{t("delete")}</span>
                    </Popconfirm>
                  </Flex>
                </Menu.Item>
              </Menu>
            }
          />
        </div>
      ),
    },
  ];

  return (
    <Card>
      <Flex alignItems="between" justifyContent="between" mobileFlex={false}>
        <Flex alignItems="end" className="mb-1" mobileFlex={false}>
          <div>
            <Button onClick={() => setAddGroupModalVisible(true)} type="primary" icon={<PlusCircleOutlined />} block>
              {t("add_group")}
            </Button>
          </div>
        </Flex>
      </Flex>
      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={tableColumns}
          dataSource={groups}
          rowKey="id"
          loading={companiesLoading || groupsLoading || productsLoading}
        />
      </div>
      <Modal
        title={t("add_group")}
        open={addGroupModalVisible}
        onCancel={() => closeAddModal()}
        onOk={handleAddGroup}
        okText={t("save")}
        cancelText={t("close")}
      >
        <Form layout="vertical" form={addGroupForm} name="advanced_search" className="ant-advanced-search-form">
          <Form.Item
            name="name"
            label={t("group_name")}
            rules={[
              {
                required: true,
                message: t("form.enter_group_name"),
              },
            ]}
          >
            <Input
              placeholder={t("group_name")}
              suffix={
                <Tooltip title={t("form.add_group_name_tooltip")}>
                  <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
                </Tooltip>
              }
            />
          </Form.Item>
          <Form.Item name="customers" label={t("customers")}>
            <Select mode="multiple" placeholder={t("customers")} showSearch optionFilterProp="children">
              {companies.length > 0 &&
                companies.map((customer, index) => {
                  return (
                    <Option value={customer.id} key={index}>
                      {customer.customerName}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
          <Form.Item name="products" label={t("products")}>
            <Select mode="multiple" placeholder={t("products")} showSearch optionFilterProp="children">
              {products.length > 0 &&
                products.map((product, index) => {
                  return (
                    <Option value={product.id} key={index}>
                      {`${product.productId} - ${product.productNameNL}`}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {editGroupModalVisible && (
        <Modal
          title={t("edit_group")}
          open={editGroupModalVisible}
          onCancel={() => closeEditModal()}
          onOk={handleUpdateGroup}
          okText={t("save")}
          cancelText={t("close")}
        >
          <Form layout="vertical" form={editGroupForm} name="advanced_search" className="ant-advanced-search-form">
            <Form.Item
              name="name"
              label={t("group_name")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_group_name"),
                },
              ]}
            >
              <Input
                placeholder={t("group_name")}
                suffix={
                  <Tooltip title={t("form.add_group_name_tooltip")}>
                    <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
                  </Tooltip>
                }
              />
            </Form.Item>
            <Form.Item name="customers" label={t("customers")}>
              <Select mode="multiple" placeholder={t("customers")} showSearch optionFilterProp="children">
                {companies.length > 0 &&
                  companies.map((customer, index) => {
                    return (
                      <Option value={customer.id} key={index}>
                        {customer.customerName}
                      </Option>
                    );
                  })}
              </Select>
            </Form.Item>
            <Form.Item name="products" label={t("products")}>
              <Select mode="multiple" placeholder={t("products")} showSearch optionFilterProp="children">
                {products.length > 0 &&
                  products.map((product, index) => {
                    return (
                      <Option value={product.id} key={index}>
                        {`${product.productId} - ${product.productNameNL}`}
                      </Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </Card>
  );
};

export default Groups;
